import { router } from '@inertiajs/vue3'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toValue,
  type Component,
  type MaybeRefOrGetter,
} from 'vue'

export interface PaletteCommand {
  id: string
  title: string
  /** Executes the command. Ignored when `children` is set. */
  perform?: () => void | Promise<void>
  /** Selecting a command with children drills into a sub-level listing them. */
  children?: MaybeRefOrGetter<readonly PaletteCommand[]>
  /** Multi-select toggle: Space or click toggles and keeps the palette open, Enter applies and closes. */
  toggle?: boolean
  /** Marks a toggle command as active, rendered with a checkmark. */
  active?: boolean
  /** Called debounced while the command is highlighted, e.g. to prefetch its page. */
  onHighlight?: () => void
  icon?: Component
  hint?: string
  keywords?: string[]
}

export function prefetchVisit(url: string): () => void {
  return () => {
    router.prefetch(url, { method: 'get' }, { cacheFor: '30s' })
  }
}

export interface CommandPaletteAction extends PaletteCommand {
  hidden?: boolean
}

interface ActionRegistration {
  id: symbol
  actions: MaybeRefOrGetter<readonly CommandPaletteAction[]>
}

const isOpen = ref(false)
const actionRegistrations = shallowRef<ActionRegistration[]>([])
const contextualActions = computed(() =>
  actionRegistrations.value.flatMap((registration) => toValue(registration.actions)),
)

export function useCommandPalette() {
  return {
    isOpen,
    contextualActions,
    open: () => {
      isOpen.value = true
    },
  }
}

export function useCommandPaletteActions(
  actions: MaybeRefOrGetter<readonly CommandPaletteAction[]>,
): void {
  const registration: ActionRegistration = {
    id: Symbol('command-palette-actions'),
    actions,
  }

  onMounted(() => {
    actionRegistrations.value = [...actionRegistrations.value, registration]
  })

  onUnmounted(() => {
    actionRegistrations.value = actionRegistrations.value.filter(
      (candidate) => candidate.id !== registration.id,
    )
  })
}
